/////////////////////////////////////////////////////////
// Configurations related to user.                     //
/////////////////////////////////////////////////////////

import { BUSINESS_USER_TYPE } from '../util/userTypes';

// User types come from Console (the users/user-types.json asset) and only from
// there. User fields are defined here and merged with the hosted ones — see
// mergeUserConfig in util/configHelpers.js. Console holds no user fields for
// ZUPRIZE; the profile's username is bespoke code, not a field.

/**
 * A user field (custom extended data on the user):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              'public', 'protected' or 'private'. Default 'public'.
 * - schemaType (optional):         'enum', 'multi-enum', 'text', 'long' or 'boolean'.
 * - enumOptions (optional):        Options for 'enum' and 'multi-enum' fields.
 * - showConfig:                    How the saved value is shown.
 *   - label:                         Label for the saved data.
 *   - displayInProfile (optional):   Hide the value from the profile page. Default true.
 * - saveConfig:                    How the value is gathered.
 *   - singleLine (optional):         One line of text, like Console's "Single-line text":
 *                                    an <input> instead of a text area, stored on one line.
 *   - maxLength, autoComplete, inputMode (optional): the input's own attributes.
 *   - validateAs (optional):         A single-line field with a format: 'email' (checked,
 *                                    and linked on the profile), 'postcode' (four digits),
 *                                    'orgNumber' (nine digits, mod-11 check digit, spaces
 *                                    removed on save), 'url' (https:// added on save).
 *   - label:                         Label for the input.
 *   - placeholderMessage (optional): Placeholder for the input.
 *   - isRequired (optional):         Whether the field must be filled.
 *   - requiredMessage (optional):    Message shown when a required field is empty.
 *   - displayInSignUp (optional):    Show the input on the signup form. Default true.
 * - userTypeConfig:                Which user types the field belongs to.
 *   - limitToUserTypeIds:            Whether to limit the field to certain user types.
 *   - userTypeIds:                   The user types the field is relevant for.
 */

const businessOnly = {
  limitToUserTypeIds: true,
  userTypeIds: [BUSINESS_USER_TYPE],
};

/**
 * The business seller's details, for the KBM pilot.
 *
 * Every field is limited to the `business` user type, so none of them reaches
 * the public signup form or a member's profile settings. They are also
 * dropped from the merged config on any environment whose Console has no
 * `business` type yet (validUserFields keeps only fields limited to a type in
 * use), which is what keeps them dormant on production until the type is
 * added there.
 *
 * Labels are plain Norwegian strings, like the listing fields in
 * configListing.js and like labels that come from Console: the signup form,
 * profile settings and the profile page show a field's label as it is,
 * without going through react-intl.
 */
export const userFields = [
  {
    key: 'companyName',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Firmanavn',
    },
    saveConfig: {
      label: 'Firmanavn',
      placeholderMessage: 'Juridisk navn, som i Brønnøysundregistrene',
      isRequired: true,
      requiredMessage: 'Firmanavn må fylles ut',
      displayInSignUp: true,
      singleLine: true,
      maxLength: 120,
      autoComplete: 'organization',
    },
    userTypeConfig: businessOnly,
  },
  {
    key: 'orgNumber',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Organisasjonsnummer',
    },
    saveConfig: {
      label: 'Organisasjonsnummer',
      placeholderMessage: '123 456 789',
      isRequired: true,
      requiredMessage: 'Organisasjonsnummer må fylles ut',
      displayInSignUp: true,
      validateAs: 'orgNumber',
      inputMode: 'numeric',
      maxLength: 11,
      autoComplete: 'off',
    },
    userTypeConfig: businessOnly,
  },
  // The address is three fields, so each can be required and the postcode
  // checked. The profile shows them together as one address line
  // (BUSINESS_ADDRESS_KEYS in util/userTypes.js).
  {
    key: 'streetAddress',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Gateadresse',
    },
    saveConfig: {
      label: 'Gateadresse',
      placeholderMessage: 'For eksempel Storgata 1',
      isRequired: true,
      requiredMessage: 'Gateadresse må fylles ut',
      displayInSignUp: true,
      singleLine: true,
      maxLength: 100,
      autoComplete: 'address-line1',
    },
    userTypeConfig: businessOnly,
  },
  {
    key: 'postalCode',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Postnummer',
    },
    saveConfig: {
      label: 'Postnummer',
      placeholderMessage: '4 siffer',
      isRequired: true,
      requiredMessage: 'Postnummer må fylles ut',
      displayInSignUp: true,
      validateAs: 'postcode',
      inputMode: 'numeric',
      maxLength: 4,
      autoComplete: 'postal-code',
    },
    userTypeConfig: businessOnly,
  },
  {
    key: 'city',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Poststed',
    },
    saveConfig: {
      label: 'Poststed',
      placeholderMessage: 'For eksempel Oslo',
      isRequired: true,
      requiredMessage: 'Poststed må fylles ut',
      displayInSignUp: true,
      singleLine: true,
      maxLength: 60,
      autoComplete: 'address-level2',
    },
    userTypeConfig: businessOnly,
  },
  {
    // How a buyer reaches the partner. The terms and the privacy policy both
    // promise the partner's contact details on its profile, and a withdrawal
    // can be sent here, so it is required, public and shown as a mailto link.
    key: 'contactEmail',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'E-post',
    },
    saveConfig: {
      label: 'E-post for kundehenvendelser',
      placeholderMessage: 'post@bedriften.no',
      isRequired: true,
      requiredMessage: 'E-post for kundehenvendelser må fylles ut',
      displayInSignUp: true,
      validateAs: 'email',
      maxLength: 120,
      autoComplete: 'email',
    },
    userTypeConfig: businessOnly,
  },
  {
    key: 'website',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Nettside',
    },
    saveConfig: {
      label: 'Nettside',
      placeholderMessage: 'bedriften.no',
      isRequired: false,
      displayInSignUp: true,
      validateAs: 'url',
      maxLength: 200,
      autoComplete: 'url',
    },
    userTypeConfig: businessOnly,
  },
];

/**
 * No user types are defined in code: Console is their only source, so that a
 * type added there (dev first, then live) is the one the app sees.
 */
export const userTypes = [];
